import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  GAME_OPTIONS,
  HOLDEM_PHASES,
  PARTY_LIMITS,
  VALID_SIDE_BETS,
  SIDE_BETS,
  gameLabel,
  withPendingRules,
  type CalloutMode,
  type PartyPlan,
  type RoomState,
  type SideBetId,
  type TableRules,
} from '@tv-casino/shared';
import { GamePicker } from './GamePicker.js';
import { gamePollModel } from '@tv-casino/viewmodels/components/gamePollModel.js';
import { DEFAULT_PARTY_DRAFT, partyDraftSummary, toggleDraftGame } from '@tv-casino/viewmodels/components/partyModel.js';
import { OFF_PATH, ON_PATH } from './SoundButton.js';
import styles from './HostPanel.module.css';

/** A rules patch is a partial: only the keys the host actually touched. */
export type RulesPatch = Partial<Record<keyof TableRules, unknown>>;
/** Same idea for the Hold'em table. */
export type HoldemRulesPatch = Partial<Record<keyof RoomState['holdemRules'], unknown>>;
/** And for the baccarat table. */
export type BaccaratRulesPatch = Partial<Record<keyof RoomState['baccaratRules'], unknown>>;
/** And for the roulette table. */
export type RouletteRulesPatch = Partial<Record<keyof RoomState['rouletteRules'], unknown>>;
/** And for the craps table. */
export type CrapsRulesPatch = Partial<Record<keyof RoomState['crapsRules'], unknown>>;
/** And for the three-card poker table. */
export type ThreecardRulesPatch = Partial<Record<keyof RoomState['threecardRules'], unknown>>;
/** And for the keno board. */
export type KenoRulesPatch = Partial<Record<keyof RoomState['kenoRules'], unknown>>;

interface HostPanelProps {
  state: RoomState;
  /** The host's own player id, so they cannot kick or promote themselves. */
  playerId: string;
  busy: boolean;
  onRules: (patch: RulesPatch) => void;
  onSetGame: (game: RoomState['gameType']) => void;
  /** Put the next game to the table. Ballot defaults to every game. */
  onStartPoll: () => void;
  /** End the open poll: `apply` takes the winner, otherwise it is dropped. */
  onEndPoll: (apply: boolean) => void;
  /** Start party mode: the table works through the lineup on its own. */
  onStartParty: (plan: PartyPlan) => void;
  onStopParty: () => void;
  /** Cut the game in play short and move to the next in the lineup. */
  onSkipPartyGame: () => void;
  onHoldemRules: (patch: HoldemRulesPatch) => void;
  onBaccaratRules: (patch: BaccaratRulesPatch) => void;
  onRouletteRules: (patch: RouletteRulesPatch) => void;
  onCrapsRules: (patch: CrapsRulesPatch) => void;
  onThreecardRules: (patch: ThreecardRulesPatch) => void;
  onKenoRules: (patch: KenoRulesPatch) => void;
  onForceDeal: () => void;
  onSkipTurn: () => void;
  onToggleCards: (hidden: boolean) => void;
  /** Put the casino leaderboard on the TV. Null outside a casino night. */
  onToggleLeaderboard: ((shown: boolean) => void) | null;
  /** Put the large join QR on the TV. Null when the venue is private. */
  onToggleQr: ((shown: boolean) => void) | null;
  /** Unmute or mute the TV's sound cues. */
  onToggleSound: (on: boolean) => void;
  /** Turn the TV (or only its background) up or down, 0–100. */
  onVolume: (patch: { sound?: number; ambience?: number }) => void;
  /** How much the TV dealer calls, and how loud, 0–100. */
  onCallouts: (patch: { mode?: CalloutMode; volume?: number }) => void;
  onTransfer: (playerId: string) => void;
  onKick: (playerId: string) => void;
  onGrant: (playerId: string, amount: number) => void;
}

/** The dealer's three settings: silent, the decisions, or every call. */
const CALLOUT_CHOICES: readonly (readonly [CalloutMode, string])[] = [
  ['off', 'Off'],
  ['essential', 'Decisions'],
  ['full', 'Every call'],
];

/** How long the thumb has to rest before the new level goes to the table. */
const VOLUME_SETTLE_MS = 250;

/**
 * A 0–100 slider for the TV. It moves locally while dragged and only sends
 * once the thumb settles, so a drag is one event rather than twenty; and it
 * follows the table's value whenever nothing is waiting to go out.
 */
function VolumeSlider({
  label,
  value,
  dimmed = false,
  onCommit,
}: {
  label: string;
  value: number;
  /** Muted: the level still moves, it just is not heard yet. */
  dimmed?: boolean;
  onCommit: (value: number) => void;
}) {
  const [local, setLocal] = useState(value);
  const pending = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (pending.current === undefined) setLocal(value);
  }, [value]);
  useEffect(() => () => window.clearTimeout(pending.current), []);

  return (
    <div className={`${styles.volume} ${dimmed ? styles.dimmed : ''}`}>
      <span>{label}</span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={local}
        aria-label={label}
        aria-valuetext={`${local}%`}
        onChange={(e) => {
          const next = Number(e.target.value);
          setLocal(next);
          window.clearTimeout(pending.current);
          pending.current = window.setTimeout(() => {
            pending.current = undefined;
            onCommit(next);
          }, VOLUME_SETTLE_MS);
        }}
      />
      <span className={styles.volumeValue}>{local}%</span>
    </div>
  );
}

/** A two-state button that shows its state rather than flipping its label. */
function Chip({
  pressed,
  disabled,
  onClick,
  label,
  children,
}: {
  pressed: boolean;
  disabled: boolean;
  onClick: () => void;
  label?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`${styles.chip} ${pressed ? styles.chipOn : ''}`}
      aria-pressed={pressed}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * A number the host types. It goes out on blur, and only when it moved; the
 * key remounts the input whenever the table's value changes under it.
 */
function NumberField({
  label,
  value,
  disabled,
  min = 1,
  max,
  step,
  onCommit,
}: {
  label: string;
  value: number;
  disabled: boolean;
  min?: number;
  max?: number;
  step?: number;
  onCommit: (value: number) => void;
}) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      <input
        key={value}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        defaultValue={value}
        onBlur={(e) => {
          const next = Number(e.target.value);
          if (next !== value) onCommit(next);
        }}
      />
    </label>
  );
}

function SelectField<V extends string | number>({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: V;
  options: readonly (readonly [V, string])[];
  disabled: boolean;
  onChange: (value: V) => void;
}) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      <select
        disabled={disabled}
        value={String(value)}
        onChange={(e) => {
          const hit = options.find(([v]) => String(v) === e.target.value);
          if (hit) onChange(hit[0]);
        }}
      >
        {options.map(([v, text]) => (
          <option key={String(v)} value={String(v)}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

const numbered = (ns: readonly number[]) => ns.map((n) => [n, String(n)] as const);

/** Min and max side by side — every table has the pair. */
function Limits({
  min,
  max,
  maxLabel = 'Max bet',
  disabled,
  onCommit,
}: {
  min: number;
  max: number;
  maxLabel?: string;
  disabled: boolean;
  onCommit: (patch: { minBet?: number; maxBet?: number }) => void;
}) {
  return (
    <div className={styles.pair}>
      <NumberField label="Min bet" value={min} disabled={disabled} onCommit={(minBet) => onCommit({ minBet })} />
      <NumberField label={maxLabel} value={max} disabled={disabled} onCommit={(maxBet) => onCommit({ maxBet })} />
    </div>
  );
}

function Toggles<K extends string>({
  title,
  items,
  values,
  disabled,
  onToggle,
}: {
  title?: string;
  items: readonly (readonly [K, string])[];
  values: Record<K, boolean>;
  disabled: boolean;
  onToggle: (key: K, on: boolean) => void;
}) {
  return (
    <div className={styles.toggles}>
      {title && <p className={styles.subTitle}>{title}</p>}
      <div className={styles.toggleGrid}>
        {items.map(([key, label]) => (
          <label key={key} className={styles.toggle}>
            <input
              type="checkbox"
              disabled={disabled}
              checked={values[key]}
              onChange={(e) => onToggle(key, e.target.checked)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/** Every clock the table runs, in seconds on screen and milliseconds on the wire. */
function Clocks<K extends string>({
  items,
  values,
  disabled,
  onCommit,
}: {
  items: readonly (readonly [K, string])[];
  values: Record<K, number>;
  disabled: boolean;
  onCommit: (key: K, ms: number) => void;
}) {
  return (
    <div className={styles.toggles}>
      <p className={styles.subTitle}>Clocks · seconds, 0 = off</p>
      <div className={styles.clocks}>
        {items.map(([key, label]) => (
          <NumberField
            key={key}
            label={label}
            value={Math.round(values[key] / 1000)}
            min={0}
            step={5}
            disabled={disabled}
            onCommit={(s) => onCommit(key, Math.max(0, s) * 1000)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The host's controls, tucked behind a disclosure so the betting UI stays the
 * main event on a phone. What the host reaches for mid-hand sits on top and
 * stays open; the game, the rules and the roster fold away beneath it. Rules
 * only move between rounds — the server enforces that too, and the rules fold
 * shut when a hand starts to say so.
 */
const HOUR_CHOICES = Array.from({ length: 13 }, (_, h) => [h, h === 1 ? '1 hour' : `${h} hours`] as const);
const MINUTE_CHOICES = [0, 15, 30, 45].map((m) => [m, `${m} min`] as const);
const LAP_CHOICES = Array.from({ length: 6 }, (_, i) => [i + 1, i === 0 ? 'Once' : `${i + 1} times`] as const);

/**
 * Party mode: tap games into a lineup, pick rounds or a schedule, start. Once
 * it runs, the room sees it on the TV strip; the host keeps skip and stop.
 */
function PartyControls({
  state,
  busy,
  locked,
  onStartParty,
  onStopParty,
  onSkipPartyGame,
}: Pick<HostPanelProps, 'state' | 'busy' | 'onStartParty' | 'onStopParty' | 'onSkipPartyGame'> & {
  locked: boolean;
}) {
  const [draft, setDraft] = useState<PartyPlan>(DEFAULT_PARTY_DRAFT);
  const party = state.party;

  if (party) {
    return (
      <div className={styles.sectionBody}>
        <p className={styles.hint}>
          {party.done
            ? 'The schedule has run out. The table stays on the last game.'
            : party.due
              ? 'Time is up on this game — the next one comes up after this hand.'
              : 'The table moves to the next game on its own, always between hands.'}
        </p>
        <div className={styles.buttonRow}>
          {!party.done && (
            <button type="button" className={styles.action} disabled={locked} onClick={onSkipPartyGame}>
              Skip to next game
            </button>
          )}
          <button type="button" className={styles.action} disabled={busy} onClick={onStopParty}>
            {party.done ? 'Clear party' : 'Stop party'}
          </button>
        </div>
      </div>
    );
  }

  const summary = partyDraftSummary(draft);
  const hours = Math.floor(draft.durationMinutes / 60);
  const minutes = draft.durationMinutes % 60;
  const setDuration = (h: number, m: number) =>
    setDraft((d) => ({ ...d, durationMinutes: h * 60 + m }));

  return (
    <div className={styles.sectionBody}>
      <div className={styles.chipRow} role="group" aria-label="Party lineup">
        {GAME_OPTIONS.map(({ id, label }) => {
          const order = draft.games.indexOf(id);
          return (
            <Chip
              key={id}
              pressed={order >= 0}
              disabled={busy}
              onClick={() => setDraft((d) => toggleDraftGame(d, id))}
            >
              {order >= 0 && <span className={styles.partyOrder}>{order + 1}</span>}
              {label}
            </Chip>
          );
        })}
      </div>
      <div className={styles.chipRow} role="group" aria-label="Rotate by">
        <span className={styles.chipRowLabel}>Rotate</span>
        <Chip
          pressed={draft.mode === 'rounds'}
          disabled={busy}
          onClick={() => setDraft((d) => ({ ...d, mode: 'rounds' }))}
        >
          By rounds
        </Chip>
        <Chip
          pressed={draft.mode === 'schedule'}
          disabled={busy}
          onClick={() => setDraft((d) => ({ ...d, mode: 'schedule' }))}
        >
          On a schedule
        </Chip>
      </div>
      {draft.mode === 'rounds' ? (
        <NumberField
          label="Rounds per game"
          value={draft.roundsPerGame}
          min={PARTY_LIMITS.roundsPerGame.min}
          max={PARTY_LIMITS.roundsPerGame.max}
          disabled={busy}
          onCommit={(roundsPerGame) => setDraft((d) => ({ ...d, roundsPerGame }))}
        />
      ) : (
        <>
          <div className={styles.pair}>
            <SelectField
              label="Play for"
              value={hours}
              options={HOUR_CHOICES}
              disabled={busy}
              onChange={(h) => setDuration(h, minutes)}
            />
            <SelectField
              label="And"
              value={minutes}
              options={MINUTE_CHOICES}
              disabled={busy}
              onChange={(m) => setDuration(hours, m)}
            />
          </div>
          <SelectField
            label="Through the lineup"
            value={draft.laps}
            options={LAP_CHOICES}
            disabled={busy}
            onChange={(laps) => setDraft((d) => ({ ...d, laps }))}
          />
        </>
      )}
      <p className={styles.hint}>{summary.line}</p>
      <button
        type="button"
        className={styles.action}
        disabled={locked || !summary.ok}
        onClick={() => onStartParty(draft)}
      >
        Start the party
      </button>
    </div>
  );
}

export function HostPanel(props: HostPanelProps) {
  const {
    state,
    playerId,
    busy,
    onSetGame,
    onStartPoll,
    onEndPoll,
    onForceDeal,
    onSkipTurn,
    onToggleCards,
    onToggleLeaderboard,
    onToggleQr,
    onToggleSound,
    onVolume,
    onCallouts,
    onTransfer,
    onKick,
    onGrant,
  } = props;
  const { phase, gameType } = state;
  const isHoldem = gameType === 'holdem';
  const isBaccarat = gameType === 'baccarat';
  const isRoulette = gameType === 'roulette';
  const isCraps = gameType === 'craps';
  const betweenRounds = phase === 'lobby' || phase === 'payout';
  // Votes and parties still wait for a break; the felt and the rules do not.
  const locked = busy || !betweenRounds;
  const poll = gamePollModel(state.gamePoll, { playerId, canVote: false });
  // A running party owns the game choice; one that has run out does not.
  const partyRunning = state.party !== null && !state.party.done;
  // Mid-round edits wait on the server: show the rules as they will be.
  const next = withPendingRules(state);

  const [rulesOpen, setRulesOpen] = useState(betweenRounds);

  const nextBadge = !betweenRounds && <span className={styles.lockBadge}>Next round</span>;

  return (
    <details className={styles.panel}>
      <summary className={styles.summary}>Host controls</summary>

      <div className={styles.body}>
        <section className={styles.group}>
          <div className={styles.buttonRow}>
            {!isHoldem && (
              <button
                type="button"
                className={styles.action}
                disabled={busy || phase !== 'betting'}
                onClick={onForceDeal}
              >
                Deal now
              </button>
            )}
            {/* Nobody is ever on the clock in baccarat: a coup has no decisions.
                In roulette the same control force-spins a stalled window, and
                in craps it rolls for a shooter who has gone quiet. */}
            {!isBaccarat && (
              <button
                type="button"
                className={styles.action}
                disabled={
                  busy ||
                  (isRoulette
                    ? phase !== 'betting'
                    : isCraps
                      ? phase !== 'betting' && phase !== 'rolling'
                      : phase !== 'playing' &&
                        phase !== 'insurance' &&
                        !HOLDEM_PHASES.includes(phase))
                }
                onClick={onSkipTurn}
              >
                {isRoulette ? 'Spin now' : isCraps ? 'Roll now' : 'Skip turn'}
              </button>
            )}
          </div>

          {poll && (
            <div className={styles.poll}>
              <p className={styles.subTitle}>Table vote</p>
              <p className={styles.hint}>
                {poll.countLine} · {poll.leaderLine}
              </p>
              <div className={styles.buttonRow}>
                <button
                  type="button"
                  className={styles.action}
                  disabled={busy || poll.empty}
                  onClick={() => onEndPoll(true)}
                >
                  Play the winner
                </button>
                <button
                  type="button"
                  className={styles.action}
                  disabled={busy}
                  onClick={() => onEndPoll(false)}
                >
                  Cancel poll
                </button>
              </div>
            </div>
          )}

          <div className={styles.chipRow} role="group" aria-label="On the TV">
            <Chip
              pressed={state.soundOn}
              disabled={busy}
              label="TV sound"
              onClick={() => onToggleSound(!state.soundOn)}
            >
              <svg className={styles.chipIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d={state.soundOn ? ON_PATH : OFF_PATH} />
              </svg>
              Sound
            </Chip>
            <Chip
              pressed={state.cardsHidden}
              disabled={busy}
              onClick={() => onToggleCards(!state.cardsHidden)}
            >
              Hide cards
            </Chip>
            {onToggleLeaderboard && (
              <Chip
                pressed={state.leaderboardShown}
                disabled={busy}
                onClick={() => onToggleLeaderboard(!state.leaderboardShown)}
              >
                Leaderboard
              </Chip>
            )}
            {onToggleQr && (
              <Chip pressed={state.qrShown} disabled={busy} onClick={() => onToggleQr(!state.qrShown)}>
                Join QR
              </Chip>
            )}
          </div>
          <VolumeSlider
            label="TV volume"
            value={state.soundVolume}
            dimmed={!state.soundOn}
            onCommit={(sound) => onVolume({ sound })}
          />
          <VolumeSlider
            label="Background"
            value={state.ambienceVolume}
            dimmed={!state.soundOn}
            onCommit={(ambience) => onVolume({ ambience })}
          />
          <div className={styles.chipRow} role="group" aria-label="Dealer call-outs">
            <span className={styles.chipRowLabel}>Call-outs</span>
            {CALLOUT_CHOICES.map(([mode, label]) => (
              <Chip
                key={mode}
                pressed={state.calloutMode === mode}
                disabled={busy}
                onClick={() => onCallouts({ mode })}
              >
                {label}
              </Chip>
            ))}
          </div>
          <VolumeSlider
            label="Dealer voice"
            value={state.voiceVolume}
            dimmed={!state.soundOn || state.calloutMode === 'off'}
            onCommit={(volume) => onCallouts({ volume })}
          />
          {state.soundOn && (
            <p className={styles.hint}>
              A browser TV may need one tap on its Sound button first. Cast devices play straight
              away.
            </p>
          )}
        </section>

        <details className={styles.section}>
          <summary className={styles.sectionSummary}>
            <span>Next game</span>
            <span className={styles.summaryMeta}>
              {state.pendingGame ? `Up next: ${gameLabel(state.pendingGame)}` : gameLabel(gameType)}
            </span>
          </summary>
          <div className={styles.sectionBody}>
            {partyRunning && (
              <p className={styles.hint}>Party mode is picking the games. Stop it to pick by hand.</p>
            )}
            {/* A window game snaps back to the lobby for a new felt; a dealt
                hand finishes first. */}
            {!partyRunning && !betweenRounds && !isRoulette && !isCraps && gameType !== 'keno' && (
              <p className={styles.hint}>A game picked now comes up when this hand ends.</p>
            )}
            <GamePicker
              gameType={gameType}
              pending={state.pendingGame}
              locked={busy || partyRunning}
              onSelect={onSetGame}
            />
            {!poll && !partyRunning && (
              <button
                type="button"
                className={styles.action}
                disabled={locked}
                onClick={onStartPoll}
              >
                Let the table vote
              </button>
            )}
          </div>
        </details>

        <details className={styles.section}>
          <summary className={styles.sectionSummary}>
            <span>Party mode</span>
            <span className={styles.summaryMeta}>
              {state.party ? (state.party.done ? 'Finished' : 'Running') : 'Off'}
            </span>
          </summary>
          <PartyControls
            state={state}
            busy={busy}
            locked={locked}
            onStartParty={props.onStartParty}
            onStopParty={props.onStopParty}
            onSkipPartyGame={props.onSkipPartyGame}
          />
        </details>

        <details
          className={styles.section}
          open={rulesOpen}
          onToggle={(e) => setRulesOpen(e.currentTarget.open)}
        >
          <summary className={styles.sectionSummary}>
            <span>Table rules</span>
            <span className={styles.summaryMeta}>{nextBadge}</span>
          </summary>
          <div className={styles.sectionBody}>
            {!betweenRounds && <p className={styles.hint}>Changes land when this round ends.</p>}
            <NumberField
              label="Buy-in"
              value={next.rules.startingChips}
              min={100}
              step={100}
              disabled={busy}
              onCommit={(startingChips) => props.onRules({ startingChips })}
            />
            <GameRules {...props} state={next} locked={busy} />
          </div>
        </details>

        <details className={styles.section}>
          <summary className={styles.sectionSummary}>
            <span>Players</span>
            <span className={styles.summaryMeta}>{state.players.length}</span>
          </summary>
          <div className={styles.sectionBody}>
            {state.players.map((player) => (
              <details key={player.playerId} className={styles.player}>
                <summary className={styles.playerSummary}>
                  <span className={styles.playerName}>
                    {player.name}
                    {player.isHost && <span className={styles.hostMark}> ★</span>}
                    {!player.connected && <span className={styles.offline}> offline</span>}
                  </span>
                  <span className={styles.playerMeta}>
                    {player.chips.toLocaleString()} chips · seat
                    {player.seats.length > 1 ? 's ' : ' '}
                    {player.seats.map((s) => s + 1).join(', ')}
                  </span>
                </summary>
                <div className={styles.playerActions}>
                  <button
                    type="button"
                    className={styles.mini}
                    disabled={busy}
                    onClick={() => onGrant(player.playerId, 500)}
                  >
                    +500
                  </button>
                  <button
                    type="button"
                    className={styles.mini}
                    disabled={busy}
                    onClick={() => onGrant(player.playerId, -500)}
                  >
                    −500
                  </button>
                  {player.playerId !== playerId && (
                    <>
                      {state.hostPlayerId != null && (
                        <button
                          type="button"
                          className={styles.mini}
                          disabled={busy}
                          onClick={() => onTransfer(player.playerId)}
                        >
                          Make host
                        </button>
                      )}
                      <button
                        type="button"
                        className={styles.miniDanger}
                        disabled={busy}
                        onClick={() => onKick(player.playerId)}
                      >
                        Kick
                      </button>
                    </>
                  )}
                </div>
              </details>
            ))}
          </div>
        </details>
      </div>
    </details>
  );
}

/**
 * The current game's own rules, all in one order: the table's choices, the
 * limits, what is on or off, then the clocks.
 */
function GameRules({
  state,
  busy,
  locked,
  onRules,
  onHoldemRules,
  onBaccaratRules,
  onRouletteRules,
  onCrapsRules,
  onThreecardRules,
  onKenoRules,
}: HostPanelProps & { locked: boolean }) {
  const { rules, holdemRules, baccaratRules, rouletteRules, crapsRules, threecardRules, kenoRules } =
    state;

  switch (state.gameType) {
    case 'holdem':
      return (
        <>
          <SelectField
            label="Betting"
            value={holdemRules.bettingStructure}
            options={[
              ['no_limit', 'No-limit'],
              ['pot_limit', 'Pot-limit'],
            ]}
            disabled={locked}
            onChange={(bettingStructure) => onHoldemRules({ bettingStructure })}
          />
          <div className={styles.pair}>
            <NumberField
              label="Small blind"
              value={holdemRules.smallBlind}
              disabled={locked}
              onCommit={(smallBlind) => onHoldemRules({ smallBlind })}
            />
            <NumberField
              label="Big blind"
              value={holdemRules.bigBlind}
              disabled={locked}
              onCommit={(bigBlind) => onHoldemRules({ bigBlind })}
            />
          </div>
          <div className={styles.pair}>
            <NumberField
              label="Ante"
              value={holdemRules.ante}
              min={0}
              disabled={locked}
              onCommit={(ante) => onHoldemRules({ ante })}
            />
            <NumberField
              label="Blinds up every (hands)"
              value={holdemRules.blindIncreaseEvery}
              min={0}
              disabled={locked}
              onCommit={(blindIncreaseEvery) => onHoldemRules({ blindIncreaseEvery })}
            />
          </div>
          <SelectField
            label="Blind multiplier"
            value={holdemRules.blindIncreaseFactor}
            options={[1.5, 2, 3].map((f) => [f, `×${f}`] as const)}
            disabled={locked}
            onChange={(blindIncreaseFactor) => onHoldemRules({ blindIncreaseFactor })}
          />
          <Toggles
            items={[
              ['rebuyAllowed', 'Rebuys'],
              ['showAllAtShowdown', 'Show folded hands at showdown'],
            ]}
            values={holdemRules}
            disabled={locked}
            onToggle={(key, on) => onHoldemRules({ [key]: on })}
          />
          <Clocks
            items={[['actionTimeoutMs', 'Each turn']]}
            values={holdemRules}
            disabled={locked}
            onCommit={(key, ms) => onHoldemRules({ [key]: ms })}
          />
        </>
      );

    case 'baccarat':
      return (
        <>
          <SelectField
            label="Decks"
            value={baccaratRules.decks}
            options={numbered([6, 8])}
            disabled={locked}
            onChange={(decks) => onBaccaratRules({ decks })}
          />
          <SelectField
            label="Banker commission"
            value={baccaratRules.commission}
            options={[
              [0, 'None'],
              [0.025, '2.5%'],
              [0.05, '5%'],
              [0.1, '10%'],
            ]}
            disabled={locked}
            onChange={(commission) => onBaccaratRules({ commission })}
          />
          <SelectField
            label="Tie pays"
            value={baccaratRules.tiePayout}
            options={[6, 8, 9, 12].map((n) => [n, `${n}:1`] as const)}
            disabled={locked}
            onChange={(tiePayout) => onBaccaratRules({ tiePayout })}
          />
          <Limits
            min={baccaratRules.minBet}
            max={baccaratRules.maxBet}
            disabled={locked}
            onCommit={onBaccaratRules}
          />
          <Toggles
            items={[
              ['pairSideBets', 'Player / Banker pair bets'],
              ['rebuyAllowed', 'Rebuys'],
            ]}
            values={baccaratRules}
            disabled={locked}
            onToggle={(key, on) => onBaccaratRules({ [key]: on })}
          />
          {baccaratRules.pairSideBets && (
            <NumberField
              label="Pair bet cap"
              value={baccaratRules.sideBetMax}
              disabled={locked}
              onCommit={(sideBetMax) => onBaccaratRules({ sideBetMax })}
            />
          )}
          <Clocks
            items={[['betTimeoutMs', 'Betting']]}
            values={baccaratRules}
            disabled={locked}
            onCommit={(key, ms) => onBaccaratRules({ [key]: ms })}
          />
        </>
      );

    case 'roulette':
      return (
        <>
          <SelectField
            label="Wheel"
            value={rouletteRules.wheel}
            options={[
              ['european', 'European (single zero)'],
              ['american', 'American (double zero)'],
            ]}
            disabled={locked}
            onChange={(wheel) => onRouletteRules({ wheel })}
          />
          <Limits
            min={rouletteRules.minBet}
            max={rouletteRules.maxBet}
            disabled={locked}
            onCommit={onRouletteRules}
          />
          <Toggles
            items={[
              ['laPartage', 'La partage (half back on a zero)'],
              ['rebuyAllowed', 'Rebuys'],
            ]}
            values={rouletteRules}
            disabled={locked}
            onToggle={(key, on) => onRouletteRules({ [key]: on })}
          />
          <Clocks
            items={[['betTimeoutMs', 'Betting']]}
            values={rouletteRules}
            disabled={locked}
            onCommit={(key, ms) => onRouletteRules({ [key]: ms })}
          />
        </>
      );

    case 'keno':
      return (
        <>
          <SelectField
            label="Paytable"
            value={kenoRules.paytable}
            options={[
              ['classic', 'Classic — the Nevada card'],
              ['party', 'Party — friendlier low end'],
              ['longshot', 'Longshot — huge tops, cold mids'],
            ]}
            disabled={locked}
            onChange={(paytable) => onKenoRules({ paytable })}
          />
          <Limits
            min={kenoRules.minBet}
            max={kenoRules.maxBet}
            maxLabel="Max per ticket"
            disabled={locked}
            onCommit={onKenoRules}
          />
          <div className={styles.pair}>
            <NumberField
              label="Tickets per seat"
              value={kenoRules.maxTickets}
              max={10}
              disabled={locked}
              onCommit={(maxTickets) => onKenoRules({ maxTickets })}
            />
            <NumberField
              label="Spots per ticket"
              value={kenoRules.maxSpots}
              max={10}
              disabled={locked}
              onCommit={(maxSpots) => onKenoRules({ maxSpots })}
            />
          </div>
          {/* Pure paint: the server takes this one mid-draw too, which is
              when the room is actually looking at the tickets. */}
          <Toggles
            items={[['classicColors', 'Classic colors']]}
            values={kenoRules}
            disabled={busy}
            onToggle={(key, on) => onKenoRules({ [key]: on })}
          />
          <Toggles
            items={[['rebuyAllowed', 'Rebuys']]}
            values={kenoRules}
            disabled={locked}
            onToggle={(key, on) => onKenoRules({ [key]: on })}
          />
          <Clocks
            items={[['betTimeoutMs', 'Betting']]}
            values={kenoRules}
            disabled={locked}
            onCommit={(key, ms) => onKenoRules({ [key]: ms })}
          />
        </>
      );

    case 'craps':
      return (
        <>
          <Limits
            min={crapsRules.minBet}
            max={crapsRules.maxBet}
            disabled={locked}
            onCommit={onCrapsRules}
          />
          <NumberField
            label="Odds cap (× line bet)"
            value={crapsRules.maxOddsX}
            min={0}
            disabled={locked}
            onCommit={(maxOddsX) => onCrapsRules({ maxOddsX })}
          />
          <Toggles
            items={[
              ['placeEnabled', 'Place bets'],
              ['hardwaysEnabled', 'Hardways'],
              ['propsEnabled', 'One-roll props'],
              ['fieldTriple12', 'Field 12 pays 3 to 1'],
              ['rebuyAllowed', 'Rebuys'],
            ]}
            values={crapsRules}
            disabled={locked}
            onToggle={(key, on) => onCrapsRules({ [key]: on })}
          />
          {/* A stalled shooter is rolled for when the roll clock runs out. */}
          <Clocks
            items={[
              ['betTimeoutMs', 'Come-out'],
              ['rollTimeoutMs', 'Roll'],
            ]}
            values={crapsRules}
            disabled={locked}
            onCommit={(key, ms) => onCrapsRules({ [key]: ms })}
          />
        </>
      );

    case 'threecard':
      return (
        <>
          <SelectField
            label="Decks"
            value={threecardRules.decks}
            options={numbered([1, 2, 4])}
            disabled={locked}
            onChange={(decks) => onThreecardRules({ decks })}
          />
          <Limits
            min={threecardRules.minBet}
            max={threecardRules.maxBet}
            disabled={locked}
            onCommit={onThreecardRules}
          />
          <Toggles
            items={[
              ['pairPlus', 'Pair Plus side bet'],
              ['anteBonus', 'Ante bonus'],
              ['rebuyAllowed', 'Rebuys'],
            ]}
            values={threecardRules}
            disabled={locked}
            onToggle={(key, on) => onThreecardRules({ [key]: on })}
          />
          {threecardRules.pairPlus && (
            <NumberField
              label="Pair Plus cap"
              value={threecardRules.pairPlusMax}
              disabled={locked}
              onCommit={(pairPlusMax) => onThreecardRules({ pairPlusMax })}
            />
          )}
          <Clocks
            items={[
              ['betTimeoutMs', 'Betting'],
              ['actionTimeoutMs', 'Decision'],
            ]}
            values={threecardRules}
            disabled={locked}
            onCommit={(key, ms) => onThreecardRules({ [key]: ms })}
          />
        </>
      );

    default: {
      // Blackjack owns the long tail of rules.
      const sideBets = Object.fromEntries(
        VALID_SIDE_BETS.map((id) => [id, rules.sideBets.includes(id)]),
      ) as Record<SideBetId, boolean>;
      return (
        <>
          <SelectField
            label="Decks"
            value={rules.decks}
            options={numbered([1, 2, 4, 6, 8])}
            disabled={locked}
            onChange={(decks) => onRules({ decks })}
          />
          <SelectField
            label="Blackjack pays"
            value={rules.blackjackPayout}
            options={[
              [1.5, '3:2'],
              [1.2, '6:5'],
            ]}
            disabled={locked}
            onChange={(blackjackPayout) => onRules({ blackjackPayout })}
          />
          <SelectField
            label="Dealer on soft 17"
            value={rules.dealerHitsSoft17 ? 'hit' : 'stand'}
            options={[
              ['stand', 'Stands (S17)'],
              ['hit', 'Draws (H17)'],
            ]}
            disabled={locked}
            onChange={(v) => onRules({ dealerHitsSoft17: v === 'hit' })}
          />
          <SelectField
            label="Splits allowed"
            value={rules.maxSplits}
            options={[0, 1, 2, 3].map((n) => [n, n === 0 ? 'None' : `Up to ${n + 1} hands`] as const)}
            disabled={locked}
            onChange={(maxSplits) => onRules({ maxSplits })}
          />
          <SelectField
            label="Seats per player"
            value={rules.seatsPerPlayer}
            options={numbered([1, 2, 3, 4, 5, 6, 7])}
            disabled={locked}
            onChange={(seatsPerPlayer) => onRules({ seatsPerPlayer })}
          />
          <Limits min={rules.minBet} max={rules.maxBet} disabled={locked} onCommit={onRules} />
          <Toggles
            items={[
              ['doubleAfterSplit', 'Double after split'],
              ['surrenderAllowed', 'Surrender'],
              ['insuranceAllowed', 'Insurance'],
              ['resplitAces', 'Re-split aces'],
              ['hitSplitAces', 'Hit split aces'],
              ['rebuyAllowed', 'Rebuys'],
            ]}
            values={rules}
            disabled={locked}
            onToggle={(key, on) => onRules({ [key]: on })}
          />
          <Toggles
            title="Side bets"
            items={VALID_SIDE_BETS.map((id) => [id, SIDE_BETS[id]?.name ?? id] as const)}
            values={sideBets}
            disabled={locked}
            onToggle={(id, on) =>
              onRules({
                sideBets: on ? [...rules.sideBets, id] : rules.sideBets.filter((s) => s !== id),
              })
            }
          />
          <Clocks
            items={[
              ['betTimeoutMs', 'Betting'],
              ['actionTimeoutMs', 'Each turn'],
              ['insuranceTimeoutMs', 'Insurance'],
            ]}
            values={rules}
            disabled={locked}
            onCommit={(key, ms) => onRules({ [key]: ms })}
          />
        </>
      );
    }
  }
}
